/**
 * Minimal markdown-to-HTML renderer for Tendril chat messages.
 *
 * Handles the subset of markdown the AI model produces:
 *   - ###### .. # headings, --- / *** / ___ horizontal rules
 *   - GFM tables (header + separator row; alignment via classes)
 *   - **bold** and *italic*
 *   - `inline code`
 *   - Bullet lists (- item or * item) and numbered lists (1. item)
 *   - Blank-line paragraph breaks
 *
 * Output is sanitized — no raw HTML passthrough, no script injection.
 * The caller renders via dangerouslySetInnerHTML after calling this function.
 */

/**
 * Escape HTML special characters in plain text (prevents XSS when values
 * are later injected into innerHTML).
 *
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Apply inline formatting (bold, italic, inline code) to an already
 * HTML-escaped line. The order matters: code spans are processed first
 * so that bold/italic markers inside them are not parsed.
 *
 * @param {string} escaped — HTML-escaped text
 * @returns {string} — HTML with inline tags applied
 */
function applyInline(escaped) {
  // Inline code: `…` → <code>…</code>
  // We do this before bold/italic so markers inside backticks are inert.
  let result = escaped.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`);

  // Bold: **…** or __…__
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *…* or _…_ (single, not double)
  result = result.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  result = result.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

  // Links: [text](url) → <a href="url">text</a>
  //
  // Previously unsupported, so model output rendered the raw markdown verbatim
  // — the first live UAT showed "[Workshop Slides](https://example.com/slides)"
  // as literal text where a link belonged.
  //
  // SECURITY: `escaped` has already been HTML-escaped by the caller, so the URL
  // arrives with & as &amp; and quotes as entities — it cannot break out of the
  // attribute. On top of that we allow ONLY http/https/mailto, which blocks
  // javascript: and data: URIs (the standard injection vector for markdown link
  // rendering). Anything else renders as plain text rather than a link, so a
  // hostile URL degrades to inert output instead of silently disappearing.
  // rel="noopener noreferrer" prevents reverse-tabnabbing on target=_blank.
  result = result.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, text, url) => {
    const scheme = url.slice(0, 20).toLowerCase();
    const safe =
      scheme.startsWith('http://') || scheme.startsWith('https://') || scheme.startsWith('mailto:');
    if (!safe) return whole;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });

  return result;
}

/**
 * Split a table row into trimmed cell strings. Leading/trailing pipes are
 * optional; `\|` is a literal pipe inside a cell.
 *
 * @param {string} line
 * @returns {string[]}
 */
function splitTableRow(line) {
  const cells = [];
  let cur = '';
  const s = line.trim();
  for (let k = 0; k < s.length; k++) {
    if (s[k] === '\\' && s[k + 1] === '|') { cur += '|'; k++; }
    else if (s[k] === '|') { cells.push(cur); cur = ''; }
    else cur += s[k];
  }
  cells.push(cur);
  if (s.startsWith('|')) cells.shift();
  if (s.endsWith('|') && !s.endsWith('\\|')) cells.pop();
  return cells.map((c) => c.trim());
}

/**
 * Parse a table separator row (`|---|:--:|`) into per-column alignments
 * ('left' | 'center' | 'right' | ''), or null if the line is not a separator.
 *
 * @param {string|undefined} line
 * @returns {string[]|null}
 */
function parseSeparator(line) {
  if (line === undefined || !line.includes('|')) return null;
  const cells = splitTableRow(line);
  if (cells.length === 0 || !cells.every((c) => /^:?-+:?$/.test(c))) return null;
  return cells.map((c) => {
    const l = c.startsWith(':');
    const r = c.endsWith(':');
    return l && r ? 'center' : r ? 'right' : '';
  });
}

/**
 * Build the HTML for a table. Rows are padded/truncated to the header width.
 * All cell text is escaped before inline formatting is applied.
 */
function renderTable(header, aligns, rows) {
  const cols = header.length;
  const cls = (n) => (aligns[n] ? ` class="tmsg-align-${aligns[n]}"` : '');
  const cell = (tag, text, n, extra = '') =>
    `<${tag}${extra}${cls(n)}>${applyInline(escapeHtml(text))}</${tag}>`;
  const head = header.map((h, n) => cell('th', h, n, ' scope="col"')).join('');
  const body = rows
    .map((r) => {
      const tds = [];
      for (let n = 0; n < cols; n++) tds.push(cell('td', r[n] ?? '', n));
      return `<tr>${tds.join('')}</tr>`;
    })
    .join('');
  return (
    '<div class="tmsg-table-wrap" role="region" aria-label="Table" tabindex="0">' +
    `<table class="tmsg-table"><thead><tr>${head}</tr></thead>` +
    (rows.length ? `<tbody>${body}</tbody>` : '') +
    '</table></div>'
  );
}

/**
 * Convert a markdown string to a sanitized HTML string.
 *
 * @param {string} markdown
 * @returns {string} HTML string safe for use with dangerouslySetInnerHTML
 */
export function markdownToHtml(markdown) {
  if (!markdown || typeof markdown !== 'string') return '';

  // Normalize line endings
  const text = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const lines = text.split('\n');
  const html = [];

  let i = 0;
  let inList = false;  // currently inside <ul>
  let inOl = false;    // currently inside <ol>

  const closeLists = () => {
    if (inList) { html.push('</ul>'); inList = false; }
    if (inOl)   { html.push('</ol>'); inOl   = false; }
  };

  while (i < lines.length) {
    const line = lines[i];

    // --- Headings ---
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      closeLists();
      const level = headingMatch[1].length; // 1..6
      const content = applyInline(escapeHtml(headingMatch[2]));
      html.push(`<h${level} class="tmsg-h${level}">${content}</h${level}>`);
      i++;
      continue;
    }

    // --- Horizontal rule: ---, ***, ___ (checked before list items so `***`
    // is not parsed as a bullet) ---
    if (/^([-*_])\1{2,}\s*$/.test(line)) {
      closeLists();
      html.push('<hr class="tmsg-hr">');
      i++;
      continue;
    }

    // --- GFM table: header row immediately followed by a separator row.
    // A half-streamed table (no separator yet) falls through to paragraphs. ---
    if (line.includes('|')) {
      const aligns = parseSeparator(lines[i + 1]);
      const header = splitTableRow(line);
      // GFM: separator column count must match the header's.
      if (aligns && aligns.length === header.length) {
        closeLists();
        const rows = [];
        i += 2;
        while (i < lines.length && lines[i].trim() !== '' && lines[i].includes('|')) {
          rows.push(splitTableRow(lines[i]));
          i++;
        }
        html.push(renderTable(header, aligns, rows));
        continue;
      }
    }

    // --- Unordered list item: - text or * text ---
    const ulMatch = line.match(/^[\-\*]\s+(.+)$/);
    if (ulMatch) {
      closeLists();  // close any open ol first
      if (!inList) { html.push('<ul>'); inList = true; }
      const content = applyInline(escapeHtml(ulMatch[1]));
      html.push(`<li>${content}</li>`);
      i++;
      continue;
    }

    // --- Ordered list item: 1. text ---
    //
    // Preserve the author's numbering across interruptions. Any non-list line
    // (a description paragraph, a blank line) closes the <ol>; without a `start`
    // attribute the next item reopened at 1, so the first live UAT rendered a
    // three-workshop list as "1. …", "1. …", "1. …". Emitting the number the
    // model actually wrote keeps the sequence intact — and is more faithful than
    // a running counter would be, since it also honours a deliberate restart.
    const olMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (olMatch) {
      if (inList) { html.push('</ul>'); inList = false; }
      if (!inOl) {
        const startAt = parseInt(olMatch[1], 10);
        html.push(Number.isFinite(startAt) && startAt !== 1 ? `<ol start="${startAt}">` : '<ol>');
        inOl = true;
      }
      const content = applyInline(escapeHtml(olMatch[2]));
      html.push(`<li>${content}</li>`);
      i++;
      continue;
    }

    // --- Blank line: close lists, emit paragraph break ---
    if (line.trim() === '') {
      closeLists();
      // Only emit <br> if the next non-blank line is a paragraph (not a heading/list)
      // — just skip it; paragraph spacing is handled by CSS gap between elements.
      i++;
      continue;
    }

    // --- Regular paragraph line ---
    closeLists();
    const content = applyInline(escapeHtml(line));
    html.push(`<p>${content}</p>`);
    i++;
  }

  closeLists();

  return html.join('');
}
